import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import AsyncSelect from "react-select/async";
import moment from "moment";
import { toast } from "react-toastify";
import ButtonLoader from "../../../../share-components/ui/buttonLoader";
import {
  alterationOrderLookupApi,
  createAlterationRequestApi,
} from "../../../../services/order";

const emptyForm = () => ({
  order: null,
  items: {}, // line_item_id -> { length, quantity }
  requested_date: moment().format("YYYY-MM-DD"),
  note: "",
});

const CreateAlterationModal = ({ isOpen, toggle, onCreated }) => {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setErrors({});
    }
  }, [isOpen]);

  const loadOrders = (inputValue) =>
    new Promise((resolve) => {
      clearTimeout(timer);
      if (!inputValue || inputValue.trim().length < 3) {
        resolve([]);
        return;
      }
      setTimer(
        setTimeout(() => {
          alterationOrderLookupApi(inputValue)
            .then(({ response }) => {
              resolve(
                (response || []).map((order) => ({
                  value: order.id,
                  label: `${order.order_name}${order.mp_order_no ? ` / ${order.mp_order_no}` : ""}${order.customer_name ? ` - ${order.customer_name}` : ""}`,
                  order,
                }))
              );
            })
            .catch(() => resolve([]));
        }, 500)
      );
    });

  const selectedOrder = form.order ? form.order.order : null;
  const selectedIds = Object.keys(form.items);
  const hasGoddivaItems = !!selectedOrder && selectedOrder.items.some((item) => item.can_alter);
  const hasOtherItems = !!selectedOrder && selectedOrder.items.some((item) => !item.can_alter);

  const toggleItem = (item) => {
    const items = { ...form.items };
    if (items[item.id]) {
      delete items[item.id];
    } else {
      items[item.id] = { length: "", quantity: item.available_quantity > 0 ? 1 : "" };
    }
    setForm({ ...form, items });
  };

  const setItemField = (id, field, value) => {
    setForm({
      ...form,
      items: { ...form.items, [id]: { ...form.items[id], [field]: value } },
    });
  };

  // Server errors are keyed by position in the submitted items array (items.0.length, ...).
  const itemError = (id, field) => {
    const index = selectedIds.indexOf(String(id));
    const messages = errors[`items.${index}.${field}`];
    return messages ? messages[0] : null;
  };

  const fieldError = (key) => (errors[key] ? errors[key][0] : null);

  const submit = () => {
    const items = selectedIds.map((id) => ({
      id: Number(id),
      length: form.items[id].length,
      quantity: form.items[id].quantity,
      // The modal has one notes box; the schema stores notes per item, so each item gets it.
      note: form.note || null,
    }));

    setSubmitting(true);
    setErrors({});
    createAlterationRequestApi({
      order: form.order ? form.order.value : null,
      items,
      requested_date: form.requested_date || null,
    })
      .then(({ response }) => {
        toast.success(response.message || "Alteration request created successfully.", {
          position: toast.POSITION.TOP_RIGHT,
        });
        if (response.label_warning) {
          toast.warning(response.label_warning, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: false,
          });
        }
        toggle();
        onCreated();
      })
      .catch((err) => {
        if (err && err.errors) {
          setErrors(err.errors);
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Create Alteration</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label className="font-weight-bold">Order</Label>
          <AsyncSelect
            cacheOptions
            isClearable
            placeholder="Search by order number / MP order number"
            loadOptions={loadOrders}
            value={form.order}
            noOptionsMessage={({ inputValue }) =>
              inputValue && inputValue.trim().length >= 3 ? "No orders found" : "Type at least 3 characters"
            }
            onChange={(option) => {
              setForm({ ...form, order: option, items: {} });
              setErrors({});
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
          />
          {fieldError("order") && <div className="text-danger small mt-1">{fieldError("order")}</div>}
        </FormGroup>

        <FormGroup>
          <Label className="font-weight-bold">Alteration items</Label>
          {!selectedOrder && <p className="text-muted small mb-0">Select an order to see its items.</p>}
          {selectedOrder && !hasGoddivaItems && (
            <Alert color="warning" className="mb-2">
              This order has no Goddiva items, so it cannot be altered. Only Goddiva items can be altered.
            </Alert>
          )}
          {selectedOrder && hasGoddivaItems && hasOtherItems && (
            <Alert color="warning" className="mb-2">
              This order also contains items from other sellers. Those items cannot be altered; only the Goddiva items
              you select will be sent for alteration.
            </Alert>
          )}
          {selectedOrder &&
            selectedOrder.items.map((item) => {
              const checked = !!form.items[item.id];
              const disabled = !item.can_alter || (!checked && item.available_quantity < 1);
              return (
                <div key={item.id} className={`border rounded p-2 mb-2 ${item.can_alter ? "" : "bg-light text-muted"}`}>
                  <FormGroup check className="mb-0">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleItem(item)}
                      />{" "}
                      {item.product_name} ({item.sku}
                      {item.colour ? `, ${item.colour}` : ""}
                      {item.size ? `, ${item.size}` : ""}) –{" "}
                      {item.can_alter ? (
                        `available ${item.available_quantity} of ${item.ordered_quantity}`
                      ) : (
                        <span className="badge badge-secondary">Not a Goddiva item – cannot be altered</span>
                      )}
                    </Label>
                  </FormGroup>
                  {itemError(item.id, "id") && <div className="text-danger small">{itemError(item.id, "id")}</div>}
                  {checked && (
                    <div className="d-flex mt-2" style={{ gap: 12 }}>
                      <FormGroup className="mb-0" style={{ flex: 1 }}>
                        <Label className="small mb-1">Length (CM)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.items[item.id].length}
                          onChange={(e) => setItemField(item.id, "length", e.target.value)}
                          invalid={!!itemError(item.id, "length")}
                        />
                        {itemError(item.id, "length") && (
                          <div className="text-danger small">{itemError(item.id, "length")}</div>
                        )}
                      </FormGroup>
                      <FormGroup className="mb-0" style={{ flex: 1 }}>
                        <Label className="small mb-1">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          max={item.available_quantity}
                          value={form.items[item.id].quantity}
                          onChange={(e) => setItemField(item.id, "quantity", e.target.value)}
                          invalid={!!itemError(item.id, "quantity")}
                        />
                        {itemError(item.id, "quantity") && (
                          <div className="text-danger small">{itemError(item.id, "quantity")}</div>
                        )}
                      </FormGroup>
                    </div>
                  )}
                </div>
              );
            })}
          {fieldError("items") && <div className="text-danger small">{fieldError("items")}</div>}
        </FormGroup>

        <FormGroup>
          <Label className="font-weight-bold">Requested date</Label>
          <Input
            type="date"
            value={form.requested_date}
            max={moment().format("YYYY-MM-DD")}
            onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
            invalid={!!fieldError("requested_date")}
          />
          {fieldError("requested_date") && <div className="text-danger small">{fieldError("requested_date")}</div>}
        </FormGroup>

        <FormGroup className="mb-0">
          <Label className="font-weight-bold">Alteration notes</Label>
          <Input
            type="textarea"
            rows={3}
            maxLength={1000}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
          {selectedIds.some((id) => itemError(id, "note")) && (
            <div className="text-danger small">{itemError(selectedIds.find((id) => itemError(id, "note")), "note")}</div>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          Cancel
        </Button>
        <ButtonLoader
          color="primary"
          loading={submitting}
          disabled={submitting || !form.order || !hasGoddivaItems || selectedIds.length === 0}
          btntext="Create"
          onClick={submit}
        />
      </ModalFooter>
    </Modal>
  );
};

export default CreateAlterationModal;
